import Div from "@jumbo/shared/Div/Div";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import {fetchMatterStatus} from "app/utils/constants/fetchMappingData";
import {useEffect, useState} from "react";
import {useLocation} from "react-router-dom";


const MuiTableWrapper = ({
                             columns, // Table columns data array
                             tableClasses, // Classes to pass in the table
                             tablePaginationClasses, // Classes to pass in the pagination
                             hasCheckboxesColumn, // To define if table required checkboxes
                             selectedRows, // State variable to store the list of selected rows
                             setSelectedRows, // Set function for the selectedRows array
                             disableCheckboxes = false, // To set the enable/disable checkboxes
                             searchValue,
                             filterColumn,
                             formTypeValue,
                             data, // data from Fetch function
                             isFetching,
                             dateValue,
                             pageSize,
                             setItems,
                             setForm,
                             rowsSelection,
                             setRowsSelection,
                             tableContainerStyle={
                                maxHeight: "70vh",
                                overflowY: "auto"
                            },
                             ...props
                         }) => {
    // TABLE STATE DEFINED
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pageSize ?? 50);
    const [sortedColumn, setSortedColumn] = useState("updated_at");
    const [sortDirection, setSortDirection] = useState("desc");
    const [matterStatusMapping, setMatterStatusMapping] = useState({});

    const location = useLocation();
    // useEffect for checking if the checkboxes are enabled/disabled
    useEffect(() => {
        // If the checkboxes are disabled then uncheck all the checkboxes
        if (disableCheckboxes) {
            setSelectedRows([]);
        }
    }, [disableCheckboxes]);

    useEffect(() => {
        setPage(0);
    }, [searchValue, filterColumn]);

    useEffect(() => {
        if (!location.pathname.includes("/docgen/forms") && !location.pathname.includes("/docgen/matters") && !location.pathname.includes("/docgen/categories")) {
            setSelectedRows([]);
            setRowsSelection([]);
        }
    }, [filterColumn, searchValue, formTypeValue]);

    useEffect(() => {
        setRowsSelection([]);
    }, [page]);

    useEffect(() => {
        const getMatterStatus = async () => {
            const response = await fetchMatterStatus();
            setMatterStatusMapping(response);
        };
        getMatterStatus();
    }, []);

    // function for handling sort
    const handleSort = (column) => {
        if (column === sortedColumn) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortedColumn(column);
            setSortDirection("asc");
        }
    };

    // Function to handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Function to handle change rows per page
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(event.target?.value ?? 50);
        setPage(0);
    };

    // Function to select all rows in the current page
    const handleSelectAllRows = (event) => {
        const start = page * rowsPerPage;
        const end = start + rowsPerPage;

        const currentPageRows = sortedData.filter((item) => filterData(item)).slice(start, end);

        if (event.target.checked) {
            setSelectedRows(prevSelectedRows => [...new Set([...prevSelectedRows, ...currentPageRows])]);
            setRowsSelection([...new Set([...currentPageRows])]);
        } else {
            const currentPageRowsSet = new Set(currentPageRows);
            setSelectedRows(prevSelectedRows => prevSelectedRows.filter(row => !currentPageRowsSet.has(row)));
            setRowsSelection(prevSelectedRows => prevSelectedRows.filter(row => !currentPageRowsSet.has(row)));
        }
    };

    // Function to handle row select
    const handleRowSelect = (event, row) => {
        const selected = event.target.checked;
        if (selected) {
            setSelectedRows((prevState) => {
                return [...prevState, row];
            });
            setRowsSelection((prevState) => {
                return [...prevState, row];
            });
        } else {
            setSelectedRows(selectedRows.filter((r) => r.id !== row.id));
            setRowsSelection(rowsSelection.filter((r) => r.id !== row.id));
        }

        if (setForm) {
            // Extract the desired data from the selected row's
            let filingDate = null;
            if (
                row?.filing_date !== undefined &&
                row?.filing_date !== "" &&
                row?.filing_date !== null
            ) {
                filingDate = new Date(row?.filing_date + "T00:00:00");
            }

            // Extract the date and time components
            const filing_date = filingDate; // Get the date part

            const selectedData = {
                status: row?.status, // Modify this based on the actual status property
                filing_date, // Modify this based on the actual filing_date property
                remarks: row?.remarks,
                generated_form_id: row?.id
            };

            setForm(selectedData);
        }

        if (setItems) {
            // Extract the desired data from the selected row's matter_parties array
            const selectedPartiesData = (
                row?.generated_form_matter?.matter_parties || []
            )
                .filter((party) => party?.party?.office_email)
                .map((party) => {
                    return {
                        label: party?.party?.name, // Modify this based on the actual name property
                        value: party?.party?.office_email, // Modify this based on the actual email property
                        form_id: row?.id
                    };
                });

            // Update the setItems array with the extracted data
            if (setItems && selectedPartiesData?.length > 0) {
                setItems([...selectedPartiesData]);
            }
        }
    };

    /**
     * Function for handling the render of the values in the column
     * @param {String || Number} value of the column
     * @param {Function} renderMethod function to pass jsx
     * @param {row} row to provide the entire row data
     * @returns
     */
    const RenderValue = ({value, renderMethod = undefined, row}) => {
        if (renderMethod === undefined) {
            return <>{value}</>;
        } else {
            return renderMethod(value !== undefined ? value : null, row);
        }
    };

    const filterData = (item) => {
        if (filterColumn === "all" || filterColumn === "") {
            const itemString = JSON.stringify(item).toLowerCase();
            return itemString?.includes(searchValue?.trim()?.toLowerCase());
        } else if (filterColumn === "host_file_number") {
            let itemDocketNo = item.generated_form_matter?.host_file_number?.toLowerCase() || item?.host_file_number?.toLowerCase() || item?.matterData?.host_file_number?.toLowerCase();
            return itemDocketNo?.includes(searchValue?.trim()?.toLowerCase());
        } else if (filterColumn === "form_code") {
            let itemFormCode = item.generated_form?.form_code?.toLowerCase() || item?.form_code?.toLowerCase();
            return itemFormCode?.includes(searchValue?.trim()?.toLowerCase());
        } else if (filterColumn === "application_number") {
            let itemAppNo = item.generated_form_matter?.application_number?.toLowerCase() || item?.application_number?.toLowerCase();
            return itemAppNo?.includes(searchValue?.trim()?.toLowerCase());
        } else if (filterColumn === "status") {
            let itemStatus = item.status;
            if (formTypeValue !== "") {
                if (!matterStatusMapping[itemStatus]) {
                    return itemStatus?.toLowerCase() === formTypeValue.toLowerCase();
                } else {
                    return matterStatusMapping[itemStatus] === formTypeValue;
                }
            } else {
                return true;
            }
        } else if (filterColumn === "workbench_status") {
            let itemStatus = item.status.toLowerCase();
            if (formTypeValue !== "") {
                itemStatus = itemStatus === "filed" ? "filed" : "pending";
                return itemStatus === formTypeValue.toLowerCase();
            } else {
                return true;
            }
        } else if (filterColumn === "form_name") {
            let itemFormName = item.form_name?.toLowerCase();
            return itemFormName?.includes(searchValue?.trim()?.toLowerCase());
        } else if (filterColumn === "country") {
            let itemCountry = item.country?.toLowerCase();
            return itemCountry?.includes(searchValue?.trim()?.toLowerCase());
        } else if (filterColumn === "form_type") {
            let itemFormType = item.form_type?.toLowerCase();
            if (formTypeValue !== "") {
                return itemFormType === formTypeValue?.trim()?.toLowerCase();
            } else {
                return true;
            }
        } else if (filterColumn === "client") {
            let itemClient = item?.client?.toLowerCase();
            return itemClient?.includes(searchValue?.trim()?.toLowerCase());
        } else if (filterColumn === "applicant") {
            let itemClient = item?.applicant?.toLowerCase();
            return itemClient?.includes(searchValue?.trim()?.toLowerCase());
        } else if (filterColumn === "user_name") {
            let userName = `${item?.user?.first_name?.toLowerCase()} ${item?.user?.first_name?.toLowerCase()}`;
            return userName?.includes(searchValue?.trim()?.toLowerCase());
        } else if( filterColumn === "categoryName" ) {
            if (formTypeValue && formTypeValue !== "") {
                return !!item?.categoryFormMap?.some(category =>
                    String(category?.categoryId) === String(formTypeValue)
                );
            }
            return true;
        } else if (filterColumn === 'country_code'){
            const itemCountryCode = item?.country_code;
            if(formTypeValue !== ""){
                return itemCountryCode === formTypeValue;
            }else{
                return true;
            }
        } else if (["updated_at", "filing_date", "grant_date", "filingDate"].includes(filterColumn)) {
            let startDateObject = new Date(dateValue?.startDate);
            let endDateObject = new Date(dateValue?.endDate);
            const itemDateObject = new Date(item?.[filterColumn]);
            return itemDateObject.setHours(0, 0, 0, 0) >= startDateObject.setHours(0, 0, 0, 0) && itemDateObject.setHours(0, 0, 0, 0) <= endDateObject.setHours(0, 0, 0, 0);
        } else {
            return true;
        }
    };

    let sortedData = Array.isArray(data) && [...data]?.sort((a, b) => {
        let aValue = a?.generated_form_matter?.[sortedColumn] || a?.generated_form?.[sortedColumn] || a?.[sortedColumn] || a?.matterData?.[sortedColumn];
        let bValue = b?.generated_form_matter?.[sortedColumn] || b?.generated_form?.[sortedColumn] || b?.[sortedColumn] || b?.matterData?.[sortedColumn];

        if (sortedColumn === "status") {
            return sortDirection === "asc" ? a[sortedColumn]?.localeCompare(b[sortedColumn]) : b[sortedColumn]?.localeCompare(a[sortedColumn]);
        } else if (sortedColumn === "application_number" || sortedColumn === "workbenchId") {
            return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
        } else if (sortedColumn === "updated_at" || sortedColumn === "createdAt") {
            const dateA = new Date(a[sortedColumn]);
            const dateB = new Date(b[sortedColumn]);
            return sortDirection === "asc" ? (dateA - dateB) : (dateB - dateA);
        } else if (sortedColumn === "user_name") {
            aValue = `${a?.user?.first_name} ${a?.user?.last_name}`;
            bValue = `${b?.user?.first_name} ${b?.user?.last_name}`;
            return sortDirection === "asc" ? aValue?.localeCompare(bValue) : bValue?.localeCompare(aValue);
        } else {
            return sortDirection === "asc" ? aValue?.localeCompare(bValue) : bValue?.localeCompare(aValue);
        }

    });

    //#endregion

    return (
        <div className="customize-mui-table-wrapper">
            <TableContainer component={Paper} sx={tableContainerStyle}>
                <Table
                    className={`customize-mui-table ${tableClasses ? tableClasses : ""}`}
                    stickyHeader
                    size="small"
                >
                    {props?.colgroup && (
                        <colgroup>
                            {(hasCheckboxesColumn === undefined ||
                                hasCheckboxesColumn === true) && (
                                <col style={{width: "auto"}}/>
                            )}
                            {props?.colgroup?.map((item) => (
                                <col style={item}/>
                            ))}
                        </colgroup>
                    )}
                    <TableHead>
                        <TableRow>
                            {(hasCheckboxesColumn === undefined ||
                                    hasCheckboxesColumn === true) &&
                                sortedData?.length > 0 && (
                                    <TableCell
                                        sx={{cursor: "pointer", pr: "30px !important"}}
                                        padding="checkbox"
                                    >
                                        <Checkbox
                                            // indeterminate={
                                            //     data?.length > 0 &&
                                            //     selectedRows?.length > 0 &&
                                            //     selectedRows?.length < data?.length
                                            // }
                                            checked={
                                                sortedData.filter((item) => filterData(item))?.length > 0 &&
                                                rowsSelection.length === Math.min(rowsPerPage, sortedData.filter((item) => filterData(item))?.length - page * rowsPerPage)
                                            }
                                            onChange={handleSelectAllRows}
                                            disabled={disableCheckboxes}
                                            sx={{
                                                "& input": {
                                                    zIndex: "999"
                                                },
                                                "& svg": {
                                                    zIndex: "99"
                                                }
                                            }}
                                        />
                                    </TableCell>
                                )}
                            {columns.map((column) => (
                                <TableCell
                                    key={column.field}
                                    style={{ cursor: "pointer", ...(column?.style || {}) }}
                                    sx={{p: 0.8}}
                                >
                                    {column.sort === undefined || column.sort === true ? (
                                        <TableSortLabel
                                            onClick={() => handleSort(column.field)}
                                            active={sortedColumn === column.field}
                                            sx={{
                                                "&.Mui-active svg": {
                                                    color: "#fff !important"
                                                }
                                            }}
                                            direction={
                                                sortedColumn === column.field ? sortDirection : "asc"
                                            }
                                        >
                                            {column.header}
                                        </TableSortLabel>
                                    ) : (
                                        column.header
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isFetching ? (
                                // Skeleton loader
                                [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((row) => (
                                    <TableRow key={row}>
                                        {(hasCheckboxesColumn === undefined ||
                                            hasCheckboxesColumn === true) && (
                                            <TableCell padding="checkbox">
                                                <Skeleton animation={"wave"} variant="text" height={19}/>
                                            </TableCell>
                                        )}
                                        {columns.map((column) => (
                                            <TableCell sx={{p: 0.8}} key={column.field}>
                                                <Skeleton animation={"wave"} variant="text" height={19}/>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : // Table rows

                            sortedData?.length > 0 && sortedData.filter((item) => filterData(item))?.length > 0 ? (
                                // render rows
                                sortedData.filter((item) => filterData(item))?.slice((page) * rowsPerPage, (page) * rowsPerPage + rowsPerPage)?.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        selected={selectedRows.some((r) => r.id === row.id)}
                                    >
                                        {(hasCheckboxesColumn === undefined ||
                                            hasCheckboxesColumn === true) && (
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedRows.some((r) => r.id === row.id)}
                                                    onChange={(event) => handleRowSelect(event, row)}
                                                    disabled={disableCheckboxes}
                                                />
                                            </TableCell>
                                        )}
                                        {columns.map((column) => (
                                            <TableCell sx={{p: 0.8}} key={column.field}>
                                                <RenderValue
                                                    value={row[column.field]}
                                                    renderMethod={column.render}
                                                    row={row}
                                                />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                // render fallback message
                                <TableRow>
                                    <TableCell
                                        colSpan={
                                            columns?.length +
                                            (hasCheckboxesColumn === undefined ||
                                            hasCheckboxesColumn === true
                                                ? 1
                                                : 0)
                                        }
                                        align="center"
                                    >
                                        {sortedData.length > 0 ? (
                                            props.emptyBody
                                        ) : (
                                            <Div
                                                sx={{
                                                    p: "100px 10px",
                                                    backgroundColor: "#EFEFEF",

                                                    "& h6": {
                                                        fontSize: "20px",
                                                        color: "#000"
                                                    }
                                                }}
                                            >
                                                <h6 className="mb-0">No Data Found</h6>
                                            </Div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[50, 75, 100]}
                component="div"
                count={Array.isArray(sortedData) ? sortedData?.filter((item) => filterData(item))?.length || 0 : 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                className={`table-pagination ${tablePaginationClasses ? tablePaginationClasses : ""}`}
            />
        </div>
    );
};


export default MuiTableWrapper;